/**
 * Performance monitoring utilities for measuring and improving app performance
 * Integrates with Vercel Speed Insights for production monitoring
 */

const isDevelopment = import.meta.env.DEV || import.meta.env.MODE === 'development';
const isProduction = import.meta.env.PROD;

/**
 * Measures the time taken by a function to execute
 * @param name - Name of the operation to measure
 * @param fn - Function to measure
 * @returns The result of the function
 */
export function measurePerformance<T>(name: string, fn: () => T): T {
  if (!isDevelopment) return fn();
  
  const start = performance.now();
  const result = fn();
  const end = performance.now();
  
  console.log(`⏱️ ${name} took ${(end - start).toFixed(2)}ms`);
  return result;
}

/**
 * Creates a performance marker for measuring between points in code
 * @param name - Name of the performance marker
 */
export function startMeasure(name: string): void {
  if (!isDevelopment) return;
  performance.mark(`${name}-start`);
}

/**
 * Ends a performance measurement and logs the result
 * @param name - Name of the performance marker to end
 */
export function endMeasure(name: string): void {
  if (!isDevelopment) return;
  
  try {
    performance.mark(`${name}-end`);
    performance.measure(name, `${name}-start`, `${name}-end`);
    const measure = performance.getEntriesByName(name)[0];
    console.log(`⏱️ ${name}: ${measure.duration.toFixed(2)}ms`);
    
    // Clean up
    performance.clearMarks(`${name}-start`);
    performance.clearMarks(`${name}-end`);
    performance.clearMeasures(name);
  } catch (e) {
    console.error(`Failed to measure ${name}:`, e);
  }
}

/**
 * Reports metrics to the browser's Performance API and Vercel Speed Insights
 * @param metricName - Name of the metric
 * @param value - Value of the metric
 * @param tags - Additional tags for the metric
 */
export function reportMetric(metricName: string, value: number, tags?: Record<string, string>): void {
  try {
    if ('PerformanceObserver' in window) {
      const metric = {
        name: metricName,
        value,
        rating: value < 100 ? 'good' : value < 300 ? 'needs-improvement' : 'poor',
        tags: {
          ...tags,
          app: 'fitness-chat',
          environment: isDevelopment ? 'development' : 'production'
        }
      };
      
      // Development: Log to console
      if (isDevelopment) {
        console.log(`📊 ${metricName}:`, metric);
      }
      
      // Production: Send to Vercel Speed Insights via Performance API
      if (isProduction) {
        window.performance.mark(metricName, { detail: metric });
      }
    }
  } catch (e) {
    // Ignore errors in production
    if (isDevelopment) {
      console.error('Error reporting metric:', e);
    }
  }
}

/**
 * Track fitness-specific performance metrics
 */
export function trackFitnessMetric(eventType: string, duration: number, additionalData?: Record<string, unknown>): void {
  reportMetric(`fitness_${eventType}`, duration, {
    category: 'fitness',
    type: eventType,
    ...additionalData
  });
}

/**
 * Monitors Core Web Vitals metrics
 */
export function monitorWebVitals(): void {
  try {
    if ('PerformanceObserver' in window) {
      // FID observer
      new PerformanceObserver((entryList) => {
        for (const entry of entryList.getEntries()) {
          const metric = {
            // @ts-expect-error - Web Vitals types not fully available
            name: 'FID',
            // @ts-expect-error - Web Vitals types not fully available  
            value: entry.processingStart - entry.startTime,
            rating: 'needs-improvement',
          };
          if (isDevelopment) {
            console.log('FID:', metric);
          }
        }
      }).observe({ type: 'first-input', buffered: true });
      
      // LCP observer
      new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries();
        const lastEntry = entries[entries.length - 1];
        const metric = {
          name: 'LCP',
          // @ts-expect-error - Web Vitals types not fully available
          value: lastEntry.startTime,
          rating: 'good',
        };
        if (isDevelopment) {
          console.log('LCP:', metric);
        }
      }).observe({ type: 'largest-contentful-paint', buffered: true });
      
      // CLS observer
      new PerformanceObserver((entryList) => {
        let clsValue = 0;
        for (const entry of entryList.getEntries()) {
          // @ts-expect-error - Web Vitals types not fully available
          if (!entry.hadRecentInput) {
            // @ts-expect-error - Web Vitals types not fully available
            clsValue += entry.value;
          }
        }
        const metric = {
          name: 'CLS',
          value: clsValue,
          rating: clsValue > 0.1 ? 'poor' : 'good',
        };
        if (isDevelopment) {
          console.log('CLS:', metric);
        }
      }).observe({ type: 'layout-shift', buffered: true });
    }
  } catch (e) {
    // Ignore errors in production
    if (isDevelopment) {
      console.error('Error monitoring web vitals:', e);
    }
  }
}

// Export a singleton for easy access
export const Performance = {
  measure: measurePerformance,
  start: startMeasure,
  end: endMeasure,
  report: reportMetric,
  trackFitness: trackFitnessMetric,
  monitorWebVitals,
}; 